import { resolveRosterPeerId } from './engine/peerId';

/**
 * A stale fix older than this is not "sharing": the phone stopped half an hour ago (the map calls it "stale" past
 * the same 30 min, MAP_STALE_MS). Nothing ages the peer GPS store, so the display's gpsLastKnownAt is the clock.
 */
export const SHARING_MAX_STALE_MS = 30 * 60 * 1000;

/** A full user id (uuid) - what a cut-short Bluetooth alias of the same person resolves to. */
const FULL_ID_LENGTH = 36;

/**
 * The key one person is counted under: the group member they resolve to, else (a mutual follow the engine
 * roster also watches, or solo) the full id their alias is a prefix of, else their own id. Engine rows are already
 * one per roster user, so this only folds aliases that slipped through (2026-10-14 Turnstile: "2 of 1 sharing").
 */
function personKey(peerId, members, fullIds) {
  if (members.length > 0) {
    const member = resolveRosterPeerId(peerId, members);
    if (member) return member;
  }
  return resolveRosterPeerId(peerId, fullIds) ?? peerId;
}

function fullIdsOf(peers) {
  return [
    ...new Set(
      (peers ?? []).map((p) => String(p?.peerId ?? '')).filter((id) => id.length >= FULL_ID_LENGTH),
    ),
  ];
}

/** Is this row's person sharing right now: any signal, but not a fix gone silent past SHARING_MAX_STALE_MS. */
function isSharing(display, now) {
  const kind = display?.kind;
  if (!kind || kind === 'none') return false;
  if (kind === 'gps-stale') {
    const at = display.gpsLastKnownAt;
    return Number.isFinite(at) && now - at <= SHARING_MAX_STALE_MS;
  }
  return true;
}

/**
 * "N of M sharing": distinct people with a live peer, never engine rows. One friend filed twice read "2 of 1
 * sharing" (2026-10-14 Turnstile). Counted against everyone Find watches - the engine roster (lynx_event_peers) is
 * group members plus mutual follows going to the event - not group members alone, which read "0 of N" with a
 * friend on the radar (2026-10-16 audit). Use {@link findWatchedPeople} for M.
 */
export function countSharingPeople(peers, memberIds, now = Date.now()) {
  return sharingPeopleIds(peers, memberIds, now).length;
}

/** The people {@link countSharingPeople} counts, as their keys (a member's user id where they resolve to one). */
export function sharingPeopleIds(peers, memberIds, now = Date.now()) {
  const members = (memberIds ?? []).filter(Boolean);
  const fullIds = fullIdsOf(peers);
  const people = new Set();
  for (const peer of peers ?? []) {
    if (!peer?.peerId || !isSharing(peer.display, now)) continue;
    people.add(personKey(String(peer.peerId), members, fullIds));
  }
  return [...people];
}

/**
 * Everyone Find watches, once each: the group members, then each engine row that is not one of them (a mutual follow
 * from the roster), shaped like a member ({ userId, displayName, photoUrl, groupId: null }). Follow-backs the engine
 * has no row for yet are not known here; the roster itself is not exposed to the page.
 */
export function findWatchedPeople(members, peers) {
  const list = (members ?? []).filter((m) => m?.userId);
  const memberIds = list.map((m) => m.userId);
  const fullIds = fullIdsOf(peers);
  const seen = new Set(memberIds);
  const out = [...list];
  for (const peer of peers ?? []) {
    if (!peer?.peerId) continue;
    const key = personKey(String(peer.peerId), memberIds, fullIds);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      userId: key,
      displayName: peer.displayName ?? '',
      photoUrl: peer.photoUrl ?? null,
      groupId: null,
    });
  }
  return out;
}
